import type { ButtonHTMLAttributes, ComponentType, MouseEvent, Ref } from 'react'
import { cn } from '../lib/cn'
import type { Hotkey } from '../lib/hotkeys'
import { Spinner } from './Spinner'
import { Tooltip } from './Tooltip'
import type { Size } from './types'

/** Lucide-Icon oder gleich gebaute Komponente (z. B. die Bildmarke ohne Kachel) */
export type IconLike = ComponentType<{ className?: string; strokeWidth?: number | string; 'aria-hidden'?: boolean }>

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconLike
  /** Pflicht: aria-label und Tooltip */
  label: string
  variant?: 'ghost' | 'secondary'
  /** quadratisch in Kontrollhöhe */
  size?: Size
  /** Umschalter (aria-pressed), z. B. KI-Assistent offen */
  pressed?: boolean
  badge?: number | 'dot'
  /** erscheint im Tooltip */
  hotkey?: Hotkey
  loading?: boolean
  /** Tooltip unterdrücken (z. B. wenn die Schaltfläche ein Menü öffnet, das denselben Namen trägt) */
  tooltip?: boolean
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
  ref?: Ref<HTMLButtonElement>
}

const SIZE: Record<Size, string> = {
  sm: 'size-(--control-h-sm)',
  md: 'size-(--control-h)',
  lg: 'size-(--control-h-lg)',
}

/** Schaltfläche nur mit Symbol. Hat immer einen Namen (Tooltip nach 400 ms, bei Tastaturfokus sofort). */
export function IconButton({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'md',
  pressed,
  badge,
  hotkey,
  loading = false,
  tooltip = true,
  tooltipSide,
  disabled,
  className,
  onClick,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const iconClass = size === 'lg' ? 'size-5' : 'size-4 touch:size-5'
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (loading) return
    onClick?.(event)
  }
  const button = (
    <button
      {...rest}
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      disabled={disabled}
      onClick={handleClick}
      data-ui="icon-button"
      className={cn(
        'hit-area inline-flex shrink-0 items-center justify-center rounded-sm transition-colors duration-(--duration-instant) ease-snap',
        SIZE[size],
        disabled
          ? 'cursor-not-allowed text-fg-disabled'
          : variant === 'secondary'
            ? 'border border-line-strong bg-surface text-fg shadow-xs hover:bg-hover active:bg-pressed'
            : 'text-fg-muted hover:bg-hover hover:text-fg active:bg-pressed',
        variant === 'secondary' && disabled && 'border border-line bg-surface',
        pressed && !disabled && 'bg-pressed text-fg',
        className,
      )}
    >
      <span className="relative flex">
        {loading ? <Spinner className={iconClass} /> : <Icon className={iconClass} strokeWidth={1.75} aria-hidden />}
        {badge === 'dot' && (
          <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-danger" aria-hidden />
        )}
        {typeof badge === 'number' && badge > 0 && (
          <span
            className="absolute -top-2 left-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-micro tracking-normal text-on-status tabular-nums"
            aria-hidden
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
    </button>
  )
  if (!tooltip) return button
  return (
    <Tooltip content={label} hotkey={hotkey} side={tooltipSide}>
      {button}
    </Tooltip>
  )
}
